import { Archive, Pencil, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useSession } from '@/stores/session';
import { IcpSegmentDialog } from '../components/IcpSegmentDialog';
import { ICP_SEGMENT_MANAGER_ROLES } from '../constants';
import {
  useArchiveIcpSegment,
  useCreateIcpSegment,
  useIcpSegments,
  useRestoreIcpSegment,
  useUpdateIcpSegment,
} from '../queries';
import type { IcpSegment } from '../types';

/** The org's ideal customer profiles (ADR-108 / FE-ADR-069). */
export function IcpSegmentsPage() {
  const role = useSession((s) => s.role);
  const canManage = role !== null && ICP_SEGMENT_MANAGER_ROLES.includes(role);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<IcpSegment | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isPending, isError, error } = useIcpSegments(
    showArchived ? { archived: true } : {},
  );
  const create = useCreateIcpSegment();
  const update = useUpdateIcpSegment();
  const archive = useArchiveIcpSegment();
  const restore = useRestoreIcpSegment();
  const segments = data?.results ?? [];

  const openDialog = (segment?: IcpSegment) => {
    setEditing(segment);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">ICP segments</h1>
          <p className="text-muted-foreground text-sm">
            Your ideal customer profiles. Tag each lead with the one it matches.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => setShowArchived(!showArchived)}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived segments' : 'Show archived segments'}
            title={showArchived ? 'Showing archived segments' : 'Show archived segments'}
          >
            <Archive className="size-4" />
          </Button>
          {canManage ? (
            <Button onClick={() => openDialog()}>
              <Plus /> New segment
            </Button>
          ) : null}
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Profile</TableHead>
              <TableHead className="w-24 text-right">Leads</TableHead>
              {canManage ? <TableHead className="w-24 text-right">Actions</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 4 : 3} className="text-muted-foreground text-center">
                  {showArchived ? 'No archived segments.' : 'No ICP segments yet.'}
                </TableCell>
              </TableRow>
            ) : (
              segments.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">
                    {s.name}
                    {s.is_archived ? (
                      <Badge variant="outline" className="ml-2">
                        Archived
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden max-w-md truncate md:table-cell">
                    {s.description || '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{s.lead_count ?? 0}</TableCell>
                  {canManage ? (
                    <TableCell className="text-right">
                      {s.is_archived ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Restore ${s.name}`}
                          onClick={() => restore.mutate(s.id)}
                        >
                          <RotateCcw className="size-4" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={`Edit ${s.name}`}
                            onClick={() => openDialog(s)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={`Archive ${s.name}`}
                            onClick={() => archive.mutate(s.id)}
                          >
                            <Archive className="size-4" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <IcpSegmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        segment={editing}
        onSubmit={(input) =>
          editing ? update.mutateAsync({ id: editing.id, input }) : create.mutateAsync(input)
        }
      />
    </div>
  );
}
